import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Card, CardContent, CardHeader, Grid, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrievePlayersByTeamId, useRetrieveTeam } from "../ApiProvider";
import { useDeleteExternalMatch, useRetrieveExternalMatch, } from "../api/MatchApiProvider";
import { useRetrieveUserExternalMatchPermissions } from "../api/PermissionApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { findPlayerById } from "../players/utils";
import { formatScorer, formatSentOff } from "../stats/ScorerFormatter";
import { findPlayerExternalMatchStatByPlayerId } from "../stats/utils";
import { SquadCard } from "../teams/SquadCard";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { ResponsiveActionMenu, } from "../utils/ResponsiveActionMenu";
import { useNumericParams } from "../utils/useNumericParams";
import { MatchDateTimeHeader } from "./MatchDateTimeHeader";
import { MatchScoreboard } from "./MatchScoreboard";
const ExternalMatch = () => {
    const { id } = useNumericParams();
    const navigate = useNavigate();
    const [showDelete, setShowDelete] = React.useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { data: userExternalMatchPermissions, isLoading: userExternalMatchPermissionsIsLoading, error: userExternalMatchPermissionsError, } = useRetrieveUserExternalMatchPermissions({ externalMatchId: id });
    const { canModify = false, canView = false, canModifyStats = false, } = userExternalMatchPermissions || {};
    const { isLoading: externalMatchIsLoading, error: externalMatchError, data: externalMatch, } = useRetrieveExternalMatch({ id, enabled: canView });
    const { isLoading: useRetrieveTeamIsLoading, error: useRetrieveTeamError, data: team, } = useRetrieveTeam({ id: externalMatch?.team });
    const mutation = useDeleteExternalMatch({
        externalMatch,
        team,
        onMutate: () => { },
        onSuccess: () => {
            return navigate(`/teams/${externalMatch?.team}`);
        },
        onError: () => { },
    });
    const { data: allPlayers, error: playersError, isLoading: playersIsLoading, } = useRetrievePlayersByTeamId({ teamId: externalMatch?.team });
    const getPlayer = findPlayerById(allPlayers || []);
    const getPlayerExternalMatchStat = findPlayerExternalMatchStatByPlayerId(externalMatch?.playerExternalMatchStats || []);
    // externalMatch.players is the authoritative hydrated lineup. Prefer the fuller
    // roster record (squad number, etc.) where available, but fall back to the
    // summary so a player who has since left the team still renders.
    const players = externalMatch?.players?.map((summary) => getPlayer(summary.id) ?? summary);
    const scorers = players
        ?.map((player) => formatScorer(player, getPlayerExternalMatchStat(player.id)))
        .filter((scorer) => scorer !== undefined);
    const sentOff = players
        ?.map((player) => formatSentOff(player, getPlayerExternalMatchStat(player.id)))
        .filter((name) => name !== undefined);
    if (userExternalMatchPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    if (userExternalMatchPermissionsError) {
        return _jsx(ErrorView, { error: userExternalMatchPermissionsError });
    }
    if (!canView) {
        return _jsx(NotFound, {});
    }
    if ((externalMatchIsLoading && canView) ||
        mutation.isLoading ||
        playersIsLoading ||
        useRetrieveTeamIsLoading) {
        return _jsx(Loading, {});
    }
    if (id === undefined) {
        return _jsx(NotFound, {});
    }
    if (useRetrieveTeamError ||
        externalMatchError ||
        playersError ||
        !externalMatch ||
        !team) {
        return _jsx(ErrorView, {});
    }
    const actions = [
        ...(canModify || canModifyStats
            ? [
                {
                    icon: _jsx(BarChartIcon, {}),
                    label: "Edit Stats",
                    to: `/teams/${team.id}/external-matches/${id}/stats/update/`,
                },
            ]
            : []),
        ...(canModify
            ? [
                {
                    icon: _jsx(EditIcon, {}),
                    label: "Edit Match",
                    to: `/teams/${team.id}/external-matches/${id}/update`,
                },
                {
                    icon: _jsx(DeleteIcon, {}),
                    label: "Delete",
                    onClick: () => setShowDelete(true),
                },
            ]
            : []),
    ];
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: mutation.isError, message: "Failed to delete match. Please try again!" }), _jsx(DeleteDialog, { open: showDelete, title: "Delete External Match?", onCancel: () => setShowDelete(false), onConfirm: () => mutation.mutate() }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { title: _jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", children: [_jsx(Box, { sx: { flex: 1 } }), _jsx(MatchDateTimeHeader, { kickOff: externalMatch.kickOff, subtitle: _jsx(Link, { style: { textDecoration: "none" }, to: `/teams/${team.id}`, children: _jsx(Typography, { variant: "body2", color: "primary.main", sx: { "&:hover": { textDecoration: "underline" } }, children: team.name }) }) }), _jsx(Box, { sx: { flex: 1, display: "flex", justifyContent: "flex-end" }, children: (canModify || canModifyStats) && (_jsx(ResponsiveActionMenu, { actions: actions, isMobile: isMobile })) })] }) }), _jsx(CardContent, { children: _jsx(MatchScoreboard, { team1: { name: team.name, link: `/teams/${team.id}` }, team2: { name: externalMatch.opponentTeam.name }, score1: externalMatch.externalMatchResult?.teamScore, score2: externalMatch.externalMatchResult?.opponentTeamScore, scorers1: scorers, scorers2: [], sentOff1: sentOff, sentOff2: [] }) })] }), _jsx(Grid, { container: true, spacing: 2, sx: { mb: 3 }, children: _jsx(Grid, { item: true, xs: 12, children: _jsx(SquadCard, { title: _jsx(Typography, { align: "center", variant: "subtitle2", children: "Squad" }), players: players || [], getPlayerStat: getPlayerExternalMatchStat }) }) }), _jsx(Grid, { container: true, spacing: 2, sx: { mb: 3 }, children: externalMatch.externalMatchResult?.matchReport && (_jsx(Grid, { item: true, xs: 12, md: 6, children: _jsxs(Card, { children: [_jsx(CardHeader, { title: _jsx(Typography, { variant: "subtitle2", textAlign: "center", children: "Match Report" }) }), _jsx(CardContent, { children: _jsx(Typography, { variant: "body1", whiteSpace: "pre-line", children: externalMatch.externalMatchResult.matchReport }) })] }) })) })] }));
};
export default ExternalMatch;
